import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import { useAdmin } from '../contexts/AdminContext';
import { billingManager } from '../business/billing/BillingManager';

interface SubscriptionStatus {
  isChecking: boolean;
  needsSubscription: boolean;
  subscriptionInactive: boolean;
}

interface CachedStatus {
  needsSub: boolean;
  inactive: boolean;
}

const CACHE_KEY = 'gate:subscription';

function getCached(tenantId: string): CachedStatus | null {
  try {
    const raw = sessionStorage.getItem(`${CACHE_KEY}:${tenantId}`);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    // Handle legacy boolean cache format
    if (typeof parsed === 'boolean') return { needsSub: parsed, inactive: false };
    return parsed as CachedStatus;
  } catch { return null; }
}

function setCache(tenantId: string, status: CachedStatus): void {
  try { sessionStorage.setItem(`${CACHE_KEY}:${tenantId}`, JSON.stringify(status)); } catch {}
}

function computeStatus(summary: { noSubscriptionExists: boolean; isActive: boolean }): CachedStatus {
  if (summary.noSubscriptionExists) return { needsSub: true, inactive: false };
  if (!summary.isActive) return { needsSub: false, inactive: true };
  return { needsSub: false, inactive: false };
}

/**
 * Hook that checks whether the current tenant has an active subscription.
 * Uses sessionStorage to resolve instantly on subsequent page loads within the same session,
 * while still validating in the background.
 */
export function useSubscriptionStatus(): SubscriptionStatus {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { config, isLoading: configLoading } = useAppConfig();
  const { isHostAdmin, isLoading: adminLoading } = useAdmin();

  const [isChecking, setIsChecking] = useState(true);
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [subscriptionInactive, setSubscriptionInactive] = useState(false);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    const check = async () => {
      if (authLoading || configLoading || adminLoading) return;

      if (!isAuthenticated) {
        setIsChecking(false);
        return;
      }

      const tenantId = config?.currentUser?.tenantId;
      if (isHostAdmin || !tenantId) {
        setIsChecking(false);
        return;
      }

      if (hasCheckedRef.current) return;
      hasCheckedRef.current = true;

      // Use cached result to resolve immediately, then validate in background
      const cached = getCached(tenantId);
      if (cached !== null) {
        setNeedsSubscription(cached.needsSub);
        setSubscriptionInactive(cached.inactive);
        setIsChecking(false);
        // Background revalidation
        billingManager.getSubscriptionSummary()
          .then(s => {
            const status = computeStatus(s);
            setNeedsSubscription(status.needsSub);
            setSubscriptionInactive(status.inactive);
            setCache(tenantId, status);
          })
          .catch(() => {});
        return;
      }

      try {
        const summary = await billingManager.getSubscriptionSummary();
        const status = computeStatus(summary);
        setNeedsSubscription(status.needsSub);
        setSubscriptionInactive(status.inactive);
        setCache(tenantId, status);
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        setNeedsSubscription(false);
        setSubscriptionInactive(false);
      } finally {
        setIsChecking(false);
      }
    };

    check();
  }, [isAuthenticated, authLoading, configLoading, adminLoading, isHostAdmin, config]);

  return { isChecking, needsSubscription, subscriptionInactive };
}
